/* tslint:disable */
/* eslint-disable */
/**
 * MONEI API v1
 * The MONEI API is organized around REST principles. Our API is designed to be intuitive and developer-friendly.  ### Base URL  All API requests should be made to:  ``` https://api.monei.com/v1 ```  ### Environment  MONEI provides two environments:  - **Test Environment**: For development and testing without processing real payments - **Live Environment**: For processing real transactions in production  ### Client Libraries  We provide official SDKs to simplify integration:  - [PHP SDK](https://github.com/MONEI/monei-php-sdk) - [Python SDK](https://github.com/MONEI/monei-python-sdk) - [Node.js SDK](https://github.com/MONEI/monei-node-sdk) - [Postman Collection](https://postman.monei.com/)  Our SDKs handle authentication, error handling, and request formatting automatically.  You can download the OpenAPI specification from the https://js.monei.com/api/v1/openapi.json and generate your own client library using the [OpenAPI Generator](https://openapi-generator.tech/).  ### Important Requirements  - All API requests must be made over HTTPS - If you are not using our official SDKs, you **must provide a valid `User-Agent` header** with each request - Requests without proper authentication will return a `401 Unauthorized` error  ### Error Handling  The API returns consistent error codes and messages to help you troubleshoot issues. Each response includes a `statusCode` attribute indicating the outcome of your request.  ### Rate Limits  The API implements rate limiting to ensure stability. If you exceed the limits, requests will return a `429 Too Many Requests` status code.
 *
 * The version of the OpenAPI document: 1.8.3
 *
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import type {Configuration} from '../configuration';
import type {AxiosPromise, AxiosInstance, RawAxiosRequestConfig} from '../axios';
import globalAxios from 'axios';
// Some imports not used depending on template conditions
// @ts-ignore
import {
  DUMMY_BASE_URL,
  assertParamExists,
  setApiKeyToObject,
  setBasicAuthToObject,
  setBearerAuthToObject,
  setOAuthToObject,
  setSearchParams,
  serializeDataIfNeeded,
  toPathString,
  createRequestFunction
} from '../common';
// @ts-ignore
import {
  BASE_PATH,
  COLLECTION_FORMATS,
  type RequestArgs,
  BaseAPI,
  RequiredError,
  operationServerMap
} from '../base';
// @ts-ignore
import type {ActivateApplePayCertificateRequest} from '../model';
// @ts-ignore
import type {ApplePayCertificate} from '../model';
// @ts-ignore
import type {ApplePayCertificatesRemove200Response} from '../model';
// @ts-ignore
import type {BadRequestError} from '../model';
// @ts-ignore
import type {InternalServerError} from '../model';
// @ts-ignore
import type {NotFoundError} from '../model';
// @ts-ignore
import type {ServiceUnavailableError} from '../model';
// @ts-ignore
import type {UnauthorizedError} from '../model';
// @ts-ignore
import type {UnprocessableEntityError} from '../model';
// @ts-ignore
import type {UpdateApplePayCertificateRequest} from '../model';
/**
 * ApplePayCertificateApi - axios parameter creator
 * @export
 */
export const ApplePayCertificateApiAxiosParamCreator = function (configuration?: Configuration) {
  return {
    /**
     * Activates an Apple Pay certificate by uploading the signed certificate from Apple.  After creating a certificate in the Apple Developer Portal using your CSR, download the signed certificate and upload it here to activate payment processing.  ## Activation process  1. Ensure you have downloaded the signed certificate (`.cer` file) from Apple Developer Portal.  2. Convert the certificate to base64:     ```bash    base64 -i apple_pay.cer    ```  3. Call this endpoint with the base64-encoded certificate in the `cert` field.  ## Validation  The certificate is validated to ensure:  - It matches the private key generated when creating the CSR - It has a valid expiration date  Once activated, the certificate becomes available for decrypting Apple Pay payment tokens in your iOS app.  ## Notes  - A certificate can only be activated once. - The CSR is cleared after successful activation. - The expiration date is extracted and stored for monitoring.
     * @summary Activate Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {ActivateApplePayCertificateRequest} activateApplePayCertificateRequest
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    activate: async (
      id: string,
      activateApplePayCertificateRequest: ActivateApplePayCertificateRequest,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('activate', 'id', id);
      // verify required parameter 'activateApplePayCertificateRequest' is not null or undefined
      assertParamExists(
        'activate',
        'activateApplePayCertificateRequest',
        activateApplePayCertificateRequest
      );
      const localVarPath = `/apple-pay/certificates/{id}/activate`.replace(
        `{${'id'}}`,
        encodeURIComponent(String(id))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      // authentication APIKey required
      await setApiKeyToObject(localVarHeaderParameter, 'Authorization', configuration);

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        activateApplePayCertificateRequest,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions
      };
    },
    /**
     * Creates a new Apple Pay certificate by generating a Certificate Signing Request (CSR).  Use this endpoint to start the certificate creation process for accepting Apple Pay in your iOS app.  ## Setup workflow  1. **Call this endpoint** to generate a CSR. The response includes the `csr` field in base64 format.  2. **Decode the CSR** and save it as a `.certSigningRequest` file:     ```bash    echo \"CSR_VALUE\" | base64 -d > apple_pay.certSigningRequest    ```  3. **Go to [Apple Developer Portal](https://developer.apple.com/account/resources/certificates/list)**:    - Navigate to Certificates, Identifiers & Profiles    - Select your Merchant ID under Identifiers    - In the \"Apple Pay Payment Processing Certificate\" section, click \"Create Certificate\"    - Upload the `.certSigningRequest` file from step 2    - Download the signed certificate (`.cer` file)  4. **Convert the certificate to base64**:     ```bash    base64 -i apple_pay.cer    ```  5. **Activate the certificate** using the [Activate Certificate](https://docs.monei.com/apis/rest/apple-pay-certificates-activate/) endpoint with the base64 value.  ## Important notes  - **CSRs expire after 24 hours.** Complete the certificate creation in Apple Developer Portal within this window, or generate a new CSR. - Each CSR can only be used once. If you need a new certificate, create a new CSR. - The private key is securely generated and stored by MONEI. You never need to handle private keys directly. - You must be enrolled in the [Apple Developer Program](https://developer.apple.com/programs/) to create certificates. - The CSR is cleared from the response after the certificate is activated.
     * @summary Create Certificate
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    create: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
      const localVarPath = `/apple-pay/certificates`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      // authentication APIKey required
      await setApiKeyToObject(localVarHeaderParameter, 'Authorization', configuration);

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions
      };
    },
    /**
     * Retrieves a specific Apple Pay certificate by its ID.  Returns the certificate object with its current status, CSR (if not yet activated), and expiration date (if activated).
     * @summary Get Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    get: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('get', 'id', id);
      const localVarPath = `/apple-pay/certificates/{id}`.replace(
        `{${'id'}}`,
        encodeURIComponent(String(id))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      // authentication APIKey required
      await setApiKeyToObject(localVarHeaderParameter, 'Authorization', configuration);

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions
      };
    },
    /**
     * Retrieves all Apple Pay certificates for your account.  Returns an array of certificate objects, including both active and inactive certificates. Use this to view all certificates and their status.
     * @summary List Certificates
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    getAll: async (options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
      const localVarPath = `/apple-pay/certificates`;
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'GET', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      // authentication APIKey required
      await setApiKeyToObject(localVarHeaderParameter, 'Authorization', configuration);

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions
      };
    },
    /**
     * Deletes an Apple Pay certificate.  This permanently removes the certificate and its associated private key. The certificate will no longer be available for payment processing.  **Warning**: This action cannot be undone. If you only want to temporarily disable a certificate, use the [Update Certificate](https://docs.monei.com/apis/rest/apple-pay-certificates-update/) endpoint instead.
     * @summary Delete Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    remove: async (id: string, options: RawAxiosRequestConfig = {}): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('remove', 'id', id);
      const localVarPath = `/apple-pay/certificates/{id}`.replace(
        `{${'id'}}`,
        encodeURIComponent(String(id))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'DELETE', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      // authentication APIKey required
      await setApiKeyToObject(localVarHeaderParameter, 'Authorization', configuration);

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers
      };

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions
      };
    },
    /**
     * Updates an Apple Pay certificate\'s active status.  Use this endpoint to enable or disable a certificate. Disabling a certificate prevents it from being used for payment token decryption while keeping it available for future use.
     * @summary Update Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {UpdateApplePayCertificateRequest} updateApplePayCertificateRequest
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    update: async (
      id: string,
      updateApplePayCertificateRequest: UpdateApplePayCertificateRequest,
      options: RawAxiosRequestConfig = {}
    ): Promise<RequestArgs> => {
      // verify required parameter 'id' is not null or undefined
      assertParamExists('update', 'id', id);
      // verify required parameter 'updateApplePayCertificateRequest' is not null or undefined
      assertParamExists(
        'update',
        'updateApplePayCertificateRequest',
        updateApplePayCertificateRequest
      );
      const localVarPath = `/apple-pay/certificates/{id}`.replace(
        `{${'id'}}`,
        encodeURIComponent(String(id))
      );
      // use dummy base URL string because the URL constructor only accepts absolute URLs.
      const localVarUrlObj = new URL(localVarPath, DUMMY_BASE_URL);
      let baseOptions;
      if (configuration) {
        baseOptions = configuration.baseOptions;
      }

      const localVarRequestOptions = {method: 'POST', ...baseOptions, ...options};
      const localVarHeaderParameter = {} as any;
      const localVarQueryParameter = {} as any;

      // authentication APIKey required
      await setApiKeyToObject(localVarHeaderParameter, 'Authorization', configuration);

      localVarHeaderParameter['Content-Type'] = 'application/json';

      setSearchParams(localVarUrlObj, localVarQueryParameter);
      let headersFromBaseOptions = baseOptions && baseOptions.headers ? baseOptions.headers : {};
      localVarRequestOptions.headers = {
        ...localVarHeaderParameter,
        ...headersFromBaseOptions,
        ...options.headers
      };
      localVarRequestOptions.data = serializeDataIfNeeded(
        updateApplePayCertificateRequest,
        localVarRequestOptions,
        configuration
      );

      return {
        url: toPathString(localVarUrlObj),
        options: localVarRequestOptions
      };
    }
  };
};

/**
 * ApplePayCertificateApi - functional programming interface
 * @export
 */
export const ApplePayCertificateApiFp = function (configuration?: Configuration) {
  const localVarAxiosParamCreator = ApplePayCertificateApiAxiosParamCreator(configuration);
  return {
    /**
     * Activates an Apple Pay certificate by uploading the signed certificate from Apple.  After creating a certificate in the Apple Developer Portal using your CSR, download the signed certificate and upload it here to activate payment processing.  ## Activation process  1. Ensure you have downloaded the signed certificate (`.cer` file) from Apple Developer Portal.  2. Convert the certificate to base64:     ```bash    base64 -i apple_pay.cer    ```  3. Call this endpoint with the base64-encoded certificate in the `cert` field.  ## Validation  The certificate is validated to ensure:  - It matches the private key generated when creating the CSR - It has a valid expiration date  Once activated, the certificate becomes available for decrypting Apple Pay payment tokens in your iOS app.  ## Notes  - A certificate can only be activated once. - The CSR is cleared after successful activation. - The expiration date is extracted and stored for monitoring.
     * @summary Activate Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {ActivateApplePayCertificateRequest} activateApplePayCertificateRequest
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async activate(
      id: string,
      activateApplePayCertificateRequest: ActivateApplePayCertificateRequest,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApplePayCertificate>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.activate(
        id,
        activateApplePayCertificateRequest,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['ApplePayCertificateApi.activate']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * Creates a new Apple Pay certificate by generating a Certificate Signing Request (CSR).  Use this endpoint to start the certificate creation process for accepting Apple Pay in your iOS app.  ## Setup workflow  1. **Call this endpoint** to generate a CSR. The response includes the `csr` field in base64 format.  2. **Decode the CSR** and save it as a `.certSigningRequest` file:     ```bash    echo \"CSR_VALUE\" | base64 -d > apple_pay.certSigningRequest    ```  3. **Go to [Apple Developer Portal](https://developer.apple.com/account/resources/certificates/list)**:    - Navigate to Certificates, Identifiers & Profiles    - Select your Merchant ID under Identifiers    - In the \"Apple Pay Payment Processing Certificate\" section, click \"Create Certificate\"    - Upload the `.certSigningRequest` file from step 2    - Download the signed certificate (`.cer` file)  4. **Convert the certificate to base64**:     ```bash    base64 -i apple_pay.cer    ```  5. **Activate the certificate** using the [Activate Certificate](https://docs.monei.com/apis/rest/apple-pay-certificates-activate/) endpoint with the base64 value.  ## Important notes  - **CSRs expire after 24 hours.** Complete the certificate creation in Apple Developer Portal within this window, or generate a new CSR. - Each CSR can only be used once. If you need a new certificate, create a new CSR. - The private key is securely generated and stored by MONEI. You never need to handle private keys directly. - You must be enrolled in the [Apple Developer Program](https://developer.apple.com/programs/) to create certificates. - The CSR is cleared from the response after the certificate is activated.
     * @summary Create Certificate
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async create(
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApplePayCertificate>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.create(options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['ApplePayCertificateApi.create']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * Retrieves a specific Apple Pay certificate by its ID.  Returns the certificate object with its current status, CSR (if not yet activated), and expiration date (if activated).
     * @summary Get Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async get(
      id: string,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApplePayCertificate>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.get(id, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['ApplePayCertificateApi.get']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * Retrieves all Apple Pay certificates for your account.  Returns an array of certificate objects, including both active and inactive certificates. Use this to view all certificates and their status.
     * @summary List Certificates
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async getAll(
      options?: RawAxiosRequestConfig
    ): Promise<
      (axios?: AxiosInstance, basePath?: string) => AxiosPromise<Array<ApplePayCertificate>>
    > {
      const localVarAxiosArgs = await localVarAxiosParamCreator.getAll(options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['ApplePayCertificateApi.getAll']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * Deletes an Apple Pay certificate.  This permanently removes the certificate and its associated private key. The certificate will no longer be available for payment processing.  **Warning**: This action cannot be undone. If you only want to temporarily disable a certificate, use the [Update Certificate](https://docs.monei.com/apis/rest/apple-pay-certificates-update/) endpoint instead.
     * @summary Delete Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async remove(
      id: string,
      options?: RawAxiosRequestConfig
    ): Promise<
      (
        axios?: AxiosInstance,
        basePath?: string
      ) => AxiosPromise<ApplePayCertificatesRemove200Response>
    > {
      const localVarAxiosArgs = await localVarAxiosParamCreator.remove(id, options);
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['ApplePayCertificateApi.remove']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    },
    /**
     * Updates an Apple Pay certificate\'s active status.  Use this endpoint to enable or disable a certificate. Disabling a certificate prevents it from being used for payment token decryption while keeping it available for future use.
     * @summary Update Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {UpdateApplePayCertificateRequest} updateApplePayCertificateRequest
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    async update(
      id: string,
      updateApplePayCertificateRequest: UpdateApplePayCertificateRequest,
      options?: RawAxiosRequestConfig
    ): Promise<(axios?: AxiosInstance, basePath?: string) => AxiosPromise<ApplePayCertificate>> {
      const localVarAxiosArgs = await localVarAxiosParamCreator.update(
        id,
        updateApplePayCertificateRequest,
        options
      );
      const localVarOperationServerIndex = configuration?.serverIndex ?? 0;
      const localVarOperationServerBasePath =
        operationServerMap['ApplePayCertificateApi.update']?.[localVarOperationServerIndex]?.url;
      return (axios, basePath) =>
        createRequestFunction(
          localVarAxiosArgs,
          globalAxios,
          BASE_PATH,
          configuration
        )(axios, localVarOperationServerBasePath || basePath);
    }
  };
};

/**
 * ApplePayCertificateApi - factory interface
 * @export
 */
export const ApplePayCertificateApiFactory = function (
  configuration?: Configuration,
  basePath?: string,
  axios?: AxiosInstance
) {
  const localVarFp = ApplePayCertificateApiFp(configuration);
  return {
    /**
     * Activates an Apple Pay certificate by uploading the signed certificate from Apple.  After creating a certificate in the Apple Developer Portal using your CSR, download the signed certificate and upload it here to activate payment processing.  ## Activation process  1. Ensure you have downloaded the signed certificate (`.cer` file) from Apple Developer Portal.  2. Convert the certificate to base64:     ```bash    base64 -i apple_pay.cer    ```  3. Call this endpoint with the base64-encoded certificate in the `cert` field.  ## Validation  The certificate is validated to ensure:  - It matches the private key generated when creating the CSR - It has a valid expiration date  Once activated, the certificate becomes available for decrypting Apple Pay payment tokens in your iOS app.  ## Notes  - A certificate can only be activated once. - The CSR is cleared after successful activation. - The expiration date is extracted and stored for monitoring.
     * @summary Activate Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {ActivateApplePayCertificateRequest} activateApplePayCertificateRequest
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    activate(
      id: string,
      activateApplePayCertificateRequest: ActivateApplePayCertificateRequest,
      options?: RawAxiosRequestConfig
    ): AxiosPromise<ApplePayCertificate> {
      return localVarFp
        .activate(id, activateApplePayCertificateRequest, options)
        .then((request) => request(axios, basePath));
    },
    /**
     * Creates a new Apple Pay certificate by generating a Certificate Signing Request (CSR).  Use this endpoint to start the certificate creation process for accepting Apple Pay in your iOS app.  ## Setup workflow  1. **Call this endpoint** to generate a CSR. The response includes the `csr` field in base64 format.  2. **Decode the CSR** and save it as a `.certSigningRequest` file:     ```bash    echo \"CSR_VALUE\" | base64 -d > apple_pay.certSigningRequest    ```  3. **Go to [Apple Developer Portal](https://developer.apple.com/account/resources/certificates/list)**:    - Navigate to Certificates, Identifiers & Profiles    - Select your Merchant ID under Identifiers    - In the \"Apple Pay Payment Processing Certificate\" section, click \"Create Certificate\"    - Upload the `.certSigningRequest` file from step 2    - Download the signed certificate (`.cer` file)  4. **Convert the certificate to base64**:     ```bash    base64 -i apple_pay.cer    ```  5. **Activate the certificate** using the [Activate Certificate](https://docs.monei.com/apis/rest/apple-pay-certificates-activate/) endpoint with the base64 value.  ## Important notes  - **CSRs expire after 24 hours.** Complete the certificate creation in Apple Developer Portal within this window, or generate a new CSR. - Each CSR can only be used once. If you need a new certificate, create a new CSR. - The private key is securely generated and stored by MONEI. You never need to handle private keys directly. - You must be enrolled in the [Apple Developer Program](https://developer.apple.com/programs/) to create certificates. - The CSR is cleared from the response after the certificate is activated.
     * @summary Create Certificate
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    create(options?: RawAxiosRequestConfig): AxiosPromise<ApplePayCertificate> {
      return localVarFp.create(options).then((request) => request(axios, basePath));
    },
    /**
     * Retrieves a specific Apple Pay certificate by its ID.  Returns the certificate object with its current status, CSR (if not yet activated), and expiration date (if activated).
     * @summary Get Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    get(id: string, options?: RawAxiosRequestConfig): AxiosPromise<ApplePayCertificate> {
      return localVarFp.get(id, options).then((request) => request(axios, basePath));
    },
    /**
     * Retrieves all Apple Pay certificates for your account.  Returns an array of certificate objects, including both active and inactive certificates. Use this to view all certificates and their status.
     * @summary List Certificates
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    getAll(options?: RawAxiosRequestConfig): AxiosPromise<Array<ApplePayCertificate>> {
      return localVarFp.getAll(options).then((request) => request(axios, basePath));
    },
    /**
     * Deletes an Apple Pay certificate.  This permanently removes the certificate and its associated private key. The certificate will no longer be available for payment processing.  **Warning**: This action cannot be undone. If you only want to temporarily disable a certificate, use the [Update Certificate](https://docs.monei.com/apis/rest/apple-pay-certificates-update/) endpoint instead.
     * @summary Delete Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    remove(
      id: string,
      options?: RawAxiosRequestConfig
    ): AxiosPromise<ApplePayCertificatesRemove200Response> {
      return localVarFp.remove(id, options).then((request) => request(axios, basePath));
    },
    /**
     * Updates an Apple Pay certificate\'s active status.  Use this endpoint to enable or disable a certificate. Disabling a certificate prevents it from being used for payment token decryption while keeping it available for future use.
     * @summary Update Certificate
     * @param {string} id The unique identifier of the certificate.
     * @param {UpdateApplePayCertificateRequest} updateApplePayCertificateRequest
     * @param {*} [options] Override http request option.
     * @throws {RequiredError}
     */
    update(
      id: string,
      updateApplePayCertificateRequest: UpdateApplePayCertificateRequest,
      options?: RawAxiosRequestConfig
    ): AxiosPromise<ApplePayCertificate> {
      return localVarFp
        .update(id, updateApplePayCertificateRequest, options)
        .then((request) => request(axios, basePath));
    }
  };
};

/**
 * ApplePayCertificateApi - object-oriented interface
 * @export
 * @class ApplePayCertificateApi
 * @extends {BaseAPI}
 */
export class ApplePayCertificateApi extends BaseAPI {
  /**
   * Activates an Apple Pay certificate by uploading the signed certificate from Apple.  After creating a certificate in the Apple Developer Portal using your CSR, download the signed certificate and upload it here to activate payment processing.  ## Activation process  1. Ensure you have downloaded the signed certificate (`.cer` file) from Apple Developer Portal.  2. Convert the certificate to base64:     ```bash    base64 -i apple_pay.cer    ```  3. Call this endpoint with the base64-encoded certificate in the `cert` field.  ## Validation  The certificate is validated to ensure:  - It matches the private key generated when creating the CSR - It has a valid expiration date  Once activated, the certificate becomes available for decrypting Apple Pay payment tokens in your iOS app.  ## Notes  - A certificate can only be activated once. - The CSR is cleared after successful activation. - The expiration date is extracted and stored for monitoring.
   * @summary Activate Certificate
   * @param {string} id The unique identifier of the certificate.
   * @param {ActivateApplePayCertificateRequest} activateApplePayCertificateRequest
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof ApplePayCertificateApi
   */
  public activate(
    id: string,
    activateApplePayCertificateRequest: ActivateApplePayCertificateRequest,
    options?: RawAxiosRequestConfig
  ) {
    return ApplePayCertificateApiFp(this.configuration)
      .activate(id, activateApplePayCertificateRequest, options)
      .then((request) => request(this.axios, this.basePath));
  }

  /**
   * Creates a new Apple Pay certificate by generating a Certificate Signing Request (CSR).  Use this endpoint to start the certificate creation process for accepting Apple Pay in your iOS app.  ## Setup workflow  1. **Call this endpoint** to generate a CSR. The response includes the `csr` field in base64 format.  2. **Decode the CSR** and save it as a `.certSigningRequest` file:     ```bash    echo \"CSR_VALUE\" | base64 -d > apple_pay.certSigningRequest    ```  3. **Go to [Apple Developer Portal](https://developer.apple.com/account/resources/certificates/list)**:    - Navigate to Certificates, Identifiers & Profiles    - Select your Merchant ID under Identifiers    - In the \"Apple Pay Payment Processing Certificate\" section, click \"Create Certificate\"    - Upload the `.certSigningRequest` file from step 2    - Download the signed certificate (`.cer` file)  4. **Convert the certificate to base64**:     ```bash    base64 -i apple_pay.cer    ```  5. **Activate the certificate** using the [Activate Certificate](https://docs.monei.com/apis/rest/apple-pay-certificates-activate/) endpoint with the base64 value.  ## Important notes  - **CSRs expire after 24 hours.** Complete the certificate creation in Apple Developer Portal within this window, or generate a new CSR. - Each CSR can only be used once. If you need a new certificate, create a new CSR. - The private key is securely generated and stored by MONEI. You never need to handle private keys directly. - You must be enrolled in the [Apple Developer Program](https://developer.apple.com/programs/) to create certificates. - The CSR is cleared from the response after the certificate is activated.
   * @summary Create Certificate
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof ApplePayCertificateApi
   */
  public create(options?: RawAxiosRequestConfig) {
    return ApplePayCertificateApiFp(this.configuration)
      .create(options)
      .then((request) => request(this.axios, this.basePath));
  }

  /**
   * Retrieves a specific Apple Pay certificate by its ID.  Returns the certificate object with its current status, CSR (if not yet activated), and expiration date (if activated).
   * @summary Get Certificate
   * @param {string} id The unique identifier of the certificate.
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof ApplePayCertificateApi
   */
  public get(id: string, options?: RawAxiosRequestConfig) {
    return ApplePayCertificateApiFp(this.configuration)
      .get(id, options)
      .then((request) => request(this.axios, this.basePath));
  }

  /**
   * Retrieves all Apple Pay certificates for your account.  Returns an array of certificate objects, including both active and inactive certificates. Use this to view all certificates and their status.
   * @summary List Certificates
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof ApplePayCertificateApi
   */
  public getAll(options?: RawAxiosRequestConfig) {
    return ApplePayCertificateApiFp(this.configuration)
      .getAll(options)
      .then((request) => request(this.axios, this.basePath));
  }

  /**
   * Deletes an Apple Pay certificate.  This permanently removes the certificate and its associated private key. The certificate will no longer be available for payment processing.  **Warning**: This action cannot be undone. If you only want to temporarily disable a certificate, use the [Update Certificate](https://docs.monei.com/apis/rest/apple-pay-certificates-update/) endpoint instead.
   * @summary Delete Certificate
   * @param {string} id The unique identifier of the certificate.
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof ApplePayCertificateApi
   */
  public remove(id: string, options?: RawAxiosRequestConfig) {
    return ApplePayCertificateApiFp(this.configuration)
      .remove(id, options)
      .then((request) => request(this.axios, this.basePath));
  }

  /**
   * Updates an Apple Pay certificate\'s active status.  Use this endpoint to enable or disable a certificate. Disabling a certificate prevents it from being used for payment token decryption while keeping it available for future use.
   * @summary Update Certificate
   * @param {string} id The unique identifier of the certificate.
   * @param {UpdateApplePayCertificateRequest} updateApplePayCertificateRequest
   * @param {*} [options] Override http request option.
   * @throws {RequiredError}
   * @memberof ApplePayCertificateApi
   */
  public update(
    id: string,
    updateApplePayCertificateRequest: UpdateApplePayCertificateRequest,
    options?: RawAxiosRequestConfig
  ) {
    return ApplePayCertificateApiFp(this.configuration)
      .update(id, updateApplePayCertificateRequest, options)
      .then((request) => request(this.axios, this.basePath));
  }
}
